import { useState } from "react";

import type { ReactNode } from "react";

import { useBlocker } from "@tanstack/react-router";

import { useActualViewer } from "@softmax/web-features/session/records";

import { useStorePageEditor } from "./draft/use-store-page-editor";

import { type EditModeValue, editModeContext } from "./edit-mode-context";

type EditModeProviderProps = {
  slug: string;
  canEdit: boolean;
  canReview: boolean;
  initialEditing: boolean;
  refreshPublished: () => Promise<void>;
  children: ReactNode;
};

export function EditModeProvider(props: EditModeProviderProps) {
  const viewer = useActualViewer();
  return (
    <StorePageEditSession
      key={`${viewer.data?.subject_id ?? "anonymous"}:${props.slug}:${props.canEdit}`}
      {...props}
      trusted={viewer.isSuccess && viewer.data !== null}
    />
  );
}

function StorePageEditSession({
  slug,
  canEdit,
  canReview,
  initialEditing,
  refreshPublished,
  children,
  trusted,
}: EditModeProviderProps & { trusted: boolean }) {
  const [editing, setEditing] = useState(canEdit && initialEditing);
  const editor = useStorePageEditor({
    slug,
    enabled: editing && trusted,
    refreshPublished,
  });

  function enterEditing() {
    if (!canEdit || !trusted) return;

    setEditing(true);
  }

  function exitEditing() {
    if (editor.loading || editor.mutating) return;

    if (editor.dirty) {
      const ok = window.confirm(
        "You have unsaved changes. Exit editing and discard them?",
      );

      if (!ok) return;
    }

    editor.discardChanges();
    setEditing(false);
  }

  useBlocker({
    disabled: !editing || (!editor.dirty && !editor.mutating),
    enableBeforeUnload: editing && (editor.dirty || editor.mutating),
    shouldBlockFn: ({ current, next }) => {
      if (
        current.pathname === next.pathname &&
        JSON.stringify(current.search) === JSON.stringify(next.search)
      )
        return false;

      if (editor.mutating) return true;

      return !window.confirm(
        "You have unsaved changes. Leave and discard them?",
      );
    },
  });

  const value: EditModeValue = {
    canEdit: canEdit && trusted,
    canReview: canReview && trusted,
    editing,
    enterEditing,
    exitEditing,
    slug,
    editor,
  };

  return (
    <editModeContext.Provider value={value}>
      {children}
    </editModeContext.Provider>
  );
}
